/**
 * Вспомогательные функции для работы с view-элементами и моделью (типы, bounds, label, elementRef).
 * Все функции принимают modeler первым аргументом.
 */
import {
	FULL_ELEMENT_MAP,
	JUNCTION,
	AND_JUNCTION,
	OR_JUNCTION,
	NOTE,
	ARCHIMATE_GROUP,
	STICKER,
	STICKER_DEFAULT_SIZE,
} from './constants';

export function getBounds(modeler, viewElement) {
	const bounds = viewElement?.bounds || viewElement || {};
	const type = getType(modeler, viewElement);
	const isJunction = [JUNCTION, AND_JUNCTION, OR_JUNCTION].includes(type);
	const isSticker = type === STICKER;
	const defaultW = isJunction ? 20 : isSticker ? STICKER_DEFAULT_SIZE.width : 120;
	const defaultH = isJunction ? 20 : isSticker ? STICKER_DEFAULT_SIZE.height : 60;
	return {
		x: Math.round(bounds?.x || 0),
		y: Math.round(bounds?.y || 0),
		width: isJunction ? 20 : Math.round(bounds?.width || bounds?.w || defaultW),
		height: isJunction ? 20 : Math.round(bounds?.height || bounds?.h || defaultH),
	};
}

export function getLabel(modeler, viewElement) {
	const elementRef = getElementRef(modeler, viewElement);
	return viewElement?.label ?? elementRef?.name ?? viewElement?.name ?? '';
}

export function getDefaultLabelForType(modeler, normalizedType) {
	if (normalizedType === STICKER) return '';
	const entry = FULL_ELEMENT_MAP.get(normalizedType);
	return (entry && entry.typeName) || normalizedType || '';
}

export function getType(modeler, viewElement) {
	const elementRef = getElementRef(modeler, viewElement);
	let rawType =
		elementRef?.type ||
		elementRef?.$attrs?.['xsi:type'] ||
		elementRef?.$type ||
		viewElement?.type ||
		viewElement?.$attrs?.['xsi:type'] ||
		viewElement?.$type ||
		'';
	if (!elementRef && (!rawType || rawType === 'Node' || rawType === 'archimate:Node')) {
		rawType = NOTE;
	}
	return normalizeType(rawType);
}

export function getElementRef(modeler, viewElement) {
	const directRef = viewElement?.elementRef;
	if (directRef) {
		if (typeof directRef === 'string') {
			return (
				modeler._elementsById?.[directRef] ||
				modeler._elementsByIdentifier?.[directRef] ||
				null
			);
		}
		return directRef;
	}
	const attrRef =
		viewElement?.$attrs?.elementRef ||
		getAttrCaseInsensitive(viewElement?.$attrs, 'elementRef');
	if (attrRef) {
		return modeler._elementsById?.[attrRef] || modeler._elementsByIdentifier?.[attrRef] || null;
	}
	const refId = viewElement?.elementRefId || viewElement?.elementRefRef;
	if (refId) {
		return modeler._elementsById?.[refId] || modeler._elementsByIdentifier?.[refId] || null;
	}
	return null;
}

export function getAttrCaseInsensitive(attrs, name) {
	if (!attrs) return null;
	const target = String(name).toLowerCase();
	const key = Object.keys(attrs).find(attrKey => attrKey.toLowerCase() === target);
	return key ? attrs[key] : null;
}

export function collectElementsByIdentifier(model) {
	const map = {};
	if (!model) return map;
	const elementsNode = model.elementsNode || model.elements;
	const elements =
		elementsNode?.element ||
		elementsNode?.elements ||
		(Array.isArray(elementsNode) ? elementsNode : []);
	(elements || []).forEach(element => {
		const identifier = element?.$attrs?.identifier || element?.id;
		if (identifier) map[identifier] = element;
	});
	return map;
}

export function normalizeType(type) {
	if (!type) return '';

	const base = String(type).split(':').pop();

	if (base.includes('-')) {
		return base
			.split('-')
			.map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
			.join('');
	}

	if (base.includes('_')) {
		return base
			.split('_')
			.map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
			.join('');
	}

	return base;
}

export function isApplicationComponent(modeler, type) {
	return normalizeType(type) === 'ApplicationComponent';
}

export function isRenderableElement(modeler, type) {
	const normalizedType = normalizeType(type);
	if (normalizedType === STICKER) return true;
	if (normalizedType === 'Group' || normalizedType === ARCHIMATE_GROUP) return true;
	const entry = FULL_ELEMENT_MAP.get(normalizedType);
	return Boolean(entry && (entry.pictoRef || entry.pathMap || entry.figureRef));
}
